import { lazy, Suspense } from 'react';
import { createRoutesFromElements, Route } from 'react-router-dom';
import { ErrorMessage, LinearProgress } from 'components/fragment';
import { MainLayout } from 'components/layout';

const Auth = lazy(() => import('@/auth'));
// const DashboardPage = lazy(() => import('pages/Dashboard'));
const FileLibraryListPage = lazy(() => import('pages/FileLibrary/FLTable'));
const HospListPage = lazy(() => import('pages/HospitalRequest/HospLists'));
const LoginPage = lazy(() => import('pages/Login'));
const OrgList = lazy(() => import('pages/Organizations/OrgList'));
const OrgDetailsPage = lazy(() => import('pages/Organizations/OrgDetails'));
const PatientTransferListPage = lazy(() => import('pages/PatientTransfer/PTList'));
const PatientTransferRequestPage = lazy(() => import('pages/PatientTransfer/PTRequest'));
const PublicResourcePage = lazy(() => import('pages/PublicResource'));
const RequestAccessPage = lazy(() => import('pages/RequestAccess'));
const UsersListPage = lazy(() => import('pages/Users/TableList'));
const UserDetailsPage = lazy(() => import('pages/Users/Details'));
const VerifyAccountPage = lazy(() => import('pages/VerifyAccount'));
const TransferHospitalRequestPage = lazy(
  () => import('pages/TransferHospital/THRequest/THRequest.container')
);
const ResourceRequestListPage = lazy(
  () => import('pages/ResourceRequests/RRList/RRList.container')
);
const ResourceRequestDetailsPage = lazy(
  () => import('pages/ResourceRequests/RRDetails/RRDetails.container')
);
const ResetPasswordPage = lazy(() => import('pages/ResetPassword'));

const routesConfig = () => {
  return createRoutesFromElements(
    <Route>
      {/* Protected Routes */}
      <Route
        element={
          <Suspense fallback={<LinearProgress />}>
            <Auth />
          </Suspense>
        }>
        <Route path='dashboard' element={<MainLayout />}>
          <Route
            index
            element={
              <Suspense fallback={<LinearProgress />}>
                <PublicResourcePage />
              </Suspense>
            }
          />
          <Route path={'users'}>
            <Route
              index
              element={
                <Suspense fallback={<LinearProgress />}>
                  <UsersListPage />
                </Suspense>
              }
            />
            <Route
              path={'new'}
              element={
                <Suspense fallback={<LinearProgress />}>
                  <UserDetailsPage newEntry={true} isApproval={false} />
                </Suspense>
              }
            />
            <Route
              path={':id'}
              element={
                <Suspense fallback={<LinearProgress />}>
                  <UserDetailsPage newEntry={false} isApproval={false} />
                </Suspense>
              }
            />
          </Route>
          <Route path={'organizations'}>
            <Route
              index
              element={
                <Suspense fallback={<LinearProgress />}>
                  <OrgList />
                </Suspense>
              }
            />
            <Route
              path={'new'}
              element={
                <Suspense fallback={<LinearProgress />}>
                  <OrgDetailsPage newEntry={true} />
                </Suspense>
              }
            />
            <Route
              path={':id'}
              element={
                <Suspense fallback={<LinearProgress />}>
                  <OrgDetailsPage newEntry={false} />
                </Suspense>
              }
            />
          </Route>
          <Route path={'patient-transfer'}>
            <Route
              index
              element={
                <Suspense fallback={<LinearProgress />}>
                  <PatientTransferListPage />
                </Suspense>
              }
            />
            <Route
              path={'new'}
              element={
                <Suspense fallback={<LinearProgress />}>
                  <PatientTransferRequestPage />
                </Suspense>
              }
            />
          </Route>
          <Route path={'resource-request'}>
            <Route
              index
              element={
                <Suspense fallback={<LinearProgress />}>
                  <ResourceRequestListPage />
                </Suspense>
              }
            />
            <Route
              path={'new'}
              element={
                <Suspense fallback={<LinearProgress />}>
                  <ResourceRequestDetailsPage newEntry={true} />
                </Suspense>
              }
            />
            <Route
              path={':id'}
              element={
                <Suspense fallback={<LinearProgress />}>
                  <ResourceRequestDetailsPage newEntry={false} />
                </Suspense>
              }
            />
          </Route>
          <Route path={'file-library'}>
            <Route
              index
              element={
                <Suspense fallback={<LinearProgress />}>
                  <FileLibraryListPage />
                </Suspense>
              }
            />
          </Route>
        </Route>
      </Route>

      {/* Public Routes */}
      <Route path='/' element={<MainLayout />}>
        <Route
          index
          element={
            <Suspense fallback={<LinearProgress />}>
              <PublicResourcePage />
            </Suspense>
          }
        />
      </Route>
      <Route
        path='login'
        element={
          <Suspense fallback={<LinearProgress />}>
            <LoginPage />
          </Suspense>
        }
      />
      <Route
        path='request-access'
        element={
          <Suspense fallback={<LinearProgress />}>
            <RequestAccessPage />
          </Suspense>
        }
      />
      <Route
        path='verify_account'
        element={
          <Suspense fallback={<LinearProgress />}>
            <VerifyAccountPage />
          </Suspense>
        }
      />
      <Route
        path='resetpassword'
        element={
          <Suspense fallback={<LinearProgress />}>
            <ResetPasswordPage />
          </Suspense>
        }
      />
      <Route
        path='user_approval'
        element={
          <Suspense fallback={<LinearProgress />}>
            <UserDetailsPage newEntry={false} isApproval={true} />
          </Suspense>
        }
      />
      <Route
        path='hospital_request'
        element={
          <Suspense fallback={<LinearProgress />}>
            <HospListPage />
          </Suspense>
        }
      />
      {/*
        THIS LINK IS USED FOR THE MAGIC LINK IN THE EMAILS
      */}
      <Route
        path='transfer_hospital_request/magic-link/:id'
        element={
          <Suspense fallback={<LinearProgress />}>
            <TransferHospitalRequestPage isMagicLink={true} />
          </Suspense>
        }
      />
      <Route
        path='patient_transfer_request_list/magic-link/:id'
        element={
          <Suspense fallback={<LinearProgress />}>
            <PatientTransferListPage isMagicLink={true} />
          </Suspense>
        }
      />
      <Route
        path={'resource-request/magic-link/:id'}
        element={
          <Suspense fallback={<LinearProgress />}>
            <ResourceRequestDetailsPage newEntry={false} isMagicLink={true} />
          </Suspense>
        }
      />
      <Route
        path={'organization/link/:id'}
        element={
          <Suspense fallback={<LinearProgress />}>
            <OrgDetailsPage newEntry={false} isMagicLink={true} />
          </Suspense>
        }
      />
      <Route
        path={'organization/inventory/link/:id'}
        element={
          <Suspense fallback={<LinearProgress />}>
            <OrgDetailsPage newEntry={false} isInventoryLink={true} />
          </Suspense>
        }
      />
      <Route
        path='*'
        element={
          <div className={'hc:flex hc:w-screen hc:h-screen hc:items-center hc:justify-center'}>
            <ErrorMessage message={'Page Not Found'} />
          </div>
        }
      />
    </Route>
  );
};

export default routesConfig;
